import { ITimelineAsset } from '@/types/assetTypes';
import type { TriggerInstance } from '@/types/triggerTypes';

/**
 * Background asset categories that don't support keyframes, clips, or fade regions
 */
export const BACKGROUND_ASSET_CATEGORIES = ['camera', 'immersal', 'geospatial_anchor', 'tap_to_place', 'image_anchor'] as const;

export const NO_KEYFRAMES_CATEGORIES = ['screen_ui'] as const;

/**
 * Check if an asset is a background asset
 */
export const isBackgroundAsset = (assetCategory: string | null | undefined): boolean => {
  if (!assetCategory) return false;
  return BACKGROUND_ASSET_CATEGORIES.includes(assetCategory as any);
};


/**
 * Resolve an asset's effective end time.
 * Background assets cover the full timeline regardless of their stored endTime,
 * so they're rendered against the current timelineLength. Non-background assets
 * use their stored endTime.
 */
export const resolveAssetEndTime = (
  asset: { assetInstance?: { instanceCategory?: string | null }; endTime?: number },
  timelineLength: number
): number => {
  if (isBackgroundAsset(asset.assetInstance?.instanceCategory)) return timelineLength;
  return asset.endTime ?? 0;
};

/**
 * Resolve an asset's effective start time.
 * Background assets always begin at 0 regardless of their stored startTime —
 * a non-zero stored value (from older data or an accidental edit) would
 * otherwise leave a gap at the left edge while the right edge stays pinned
 * to timelineLength. Pairs with resolveAssetEndTime.
 */
export const resolveAssetStartTime = (
  asset: { assetInstance?: { instanceCategory?: string | null }; startTime?: number }
): number => {
  if (isBackgroundAsset(asset.assetInstance?.instanceCategory)) return 0;
  return asset.startTime ?? 0;
};

/**
 * Check if this asset type supports keyframes and clips
 */
export const supportsKeyframesAndClips = (asset: ITimelineAsset): boolean => {
  const assetCategory = asset.assetInstance?.instanceCategory;
  if (!assetCategory) return false;
  return !isBackgroundAsset(assetCategory) && !NO_KEYFRAMES_CATEGORIES.includes(assetCategory as any);
};

/**
 * Check if this asset type supports timeline resize (drag handles).
 * All non-background assets, including audio/video (for trim).
 */
export const supportsAssetResize = (asset: ITimelineAsset): boolean => {
  const category = asset.assetInstance?.instanceCategory;
  if (!category) return false;
  return !isBackgroundAsset(category);
};

/**
 * Check if asset type can show plus icon for adding clips/keyframes
 */
export const canShowPlusIcon = (type: string): boolean => {
  return type === 'characters' || type === 'props' || type === 'open_brush';
};

/**
 * Check if plus icon should be shown based on current state
 */
export const shouldShowPlusIcon = (
  hoveredItemId: string | null,
  delayedHoverId: string | null,
  assetInstanceId: string,
  isDragging: boolean,
  isResizingFade: boolean,
  isResizing: boolean,
  isLocked: boolean,
  isVisible: boolean,
  asset: ITimelineAsset,
  type: string,
  mousePosition: { x: number; y: number },
  persistentClickPosition?: {
    assetInstanceId: string;
    assetRelativeX: number;
  } | null,
  isPlaying?: boolean,
  isKeyframeDragging?: boolean,
  triggers?: TriggerInstance[],
  scale?: number
): boolean => {
  // Check if there's a persistent click position for this asset that would overlap with the plus icon
  const hasPersistentClickForThisAsset = persistentClickPosition?.assetInstanceId === assetInstanceId;
  let wouldOverlapWithDot = false;
  
  if (hasPersistentClickForThisAsset && persistentClickPosition) {
    // Calculate if the plus icon position would be close to the dot position
    // Both mousePosition.x and persistentClickPosition.assetRelativeX are asset-relative
    const distanceFromDot = Math.abs(mousePosition.x - persistentClickPosition.assetRelativeX);
    const overlapThreshold = 30; // pixels - hide plus icon if within 30px of the dot
    wouldOverlapWithDot = distanceFromDot < overlapThreshold;
  }

  // Check if mouse is near a trigger marker (same logic as clips/keyframes)
  let isNearTrigger = false;
  if (triggers && triggers.length > 0 && scale) {
    const assetDuration = asset.duration || (asset.endTime - asset.startTime) || 1;
    const triggerOverlapThreshold = 15; // pixels
    for (const trigger of triggers) {
      const relativeTime = trigger.time - asset.startTime;
      const triggerX = (relativeTime / assetDuration) * (assetDuration * (scale || 1));
      if (Math.abs(mousePosition.x - triggerX) < triggerOverlapThreshold) {
        isNearTrigger = true;
        break;
      }
    }
  }
  
  return (
    hoveredItemId === assetInstanceId &&
    delayedHoverId === assetInstanceId &&
    !isDragging &&
    !isResizingFade &&
    !isResizing &&
    !isLocked &&
    isVisible &&
    !isPlaying &&
    !isKeyframeDragging &&
    supportsKeyframesAndClips(asset) &&
    canShowPlusIcon(type) &&
    !wouldOverlapWithDot &&
    !isNearTrigger
  );
};

/**
 * Get cursor style based on current state
 */
export const getCursor = (
  isLocked: boolean,
  isDragging: boolean,
  hoveredItemId: string | null,
  delayedHoverId: string | null,
  assetInstanceId: string
): string => {
  if (isLocked) return 'not-allowed';
  if (isDragging) return 'grabbing';
  if (hoveredItemId === assetInstanceId && delayedHoverId === assetInstanceId) return 'pointer';
  return 'grab';
};
